import type { FaturaPainel, CardComparativo } from '../types.ts';
import { mesReferenciaParaData } from '../../../../src/lib/financeiro/periodo.ts';
import { STATUS_SEM_COBRANCA, valorDevido } from '../../../../src/lib/statusBoleto.ts';
import { faturasDoPainel } from '../../../../src/lib/financeiro/definicoesKpi.ts';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const fmtMes = (d: Date) => `${MESES[d.getMonth()]}/${d.getFullYear()}`;
const fmtData = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

function pct(atual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return Math.round(((atual - anterior) / anterior) * 1000) / 10;
}

// "Emitido"/"boletos emitidos" ignoram faturas sem cobrança (sem boleto no
// pipeline, de valor zero, ou sem injeção no ciclo) — mesmo critério de calcularKpis.
function ehCobranca(f: FaturaPainel) {
  return !STATUS_SEM_COBRANCA.includes(f.statusDerivado);
}

/**
 * Foto da inadimplência numa data qualquer — inclusive no passado.
 *
 * Por que reconstruir em vez de usar `statusDerivado`: aquele campo é sempre
 * "hoje". Para comparar com 30 dias atrás é preciso perguntar o que estava
 * vencido e NÃO PAGO naquele dia, e isso sai do cruzamento de vencimento com
 * data de pagamento — que a base tem.
 *
 * Cancelado e Parcelado ficam de fora nos dois lados: não são dívida em aberto.
 * Pago/Quitado SEM data de pagamento (197 casos, a confirmação manual só grava
 * o status) contam como pagos desde sempre — o contrário os transformaria em
 * dívida retroativa que nunca existiu.
 */
export function fotoInadimplencia(dados: FaturaPainel[], ref: Date) {
  const refMs = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
  const diaDe = (v: string | null | undefined): number | null => {
    if (!v) return null;
    const [ano, mes, dia] = v.split('T')[0].split('-').map(Number);
    return new Date(ano, (mes || 1) - 1, dia || 1).getTime();
  };

  let vencido = 0;
  let exigivel = 0;
  let qtde = 0;

  // Conta da própria Vigor fora daqui TAMBÉM. Sem esta linha o card contava a
  // dívida da Associação (R$ 89.737,49) que já havia sido tirada do vencido, do
  // aging e do Top Devedores — o painel mostraria dois totais de inadimplência
  // diferentes na mesma tela (achado do test-writer, 21/09/2026).
  for (const f of faturasDoPainel(dados)) {
    const cru = (f.status_boleto_vigor || '').trim().toLowerCase();
    if (cru === 'cancelado' || cru === 'parcelado') continue;
    if (!ehCobranca(f)) continue;

    const valor = valorDevido(f);
    if (!(valor > 0)) continue;

    const venc = diaDe(f.vencimento_boleto || f.data_vencimento);
    if (venc === null || venc >= refMs) continue;

    // Já era exigível naquela data — é o denominador da taxa.
    exigivel += valor;

    const pago = diaDe(f.data_pgto_boleto) ?? diaDe(f.quitado_em);
    if ((cru === 'pago' || cru === 'quitado') && pago === null) continue;
    if (pago !== null && pago <= refMs) continue;

    vencido += valor;
    qtde += 1;
  }

  return { vencido, exigivel, qtde, taxa: exigivel > 0 ? vencido / exigivel : 0 };
}

function doMes(dados: FaturaPainel[], mesRef: string) {
  return dados.filter((f) => f.mes_referencia === mesRef && ehCobranca(f));
}

type TipoComparativoMensal = 'faturamento' | 'inadimplencia' | 'boletosEmitidos';

// Cards 1-3 da seção 8: sempre mês-atual-do-relógio vs mês-anterior, independente
// do filtro de período escolhido no topo (a spec pede "Mês vs mês anterior" fixo).
export function calcularComparativoMensal(todosOsDados: FaturaPainel[], tipo: TipoComparativoMensal, hoje: Date): CardComparativo {
  const mesAtual = fmtMes(hoje);
  const mesAnteriorData = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const mesAnterior = fmtMes(mesAnteriorData);

  const dadosAtual = doMes(todosOsDados, mesAtual);
  const dadosAnterior = doMes(todosOsDados, mesAnterior);

  if (tipo === 'faturamento') {
    const atual = dadosAtual.reduce((s, f) => s + valorDevido(f), 0);
    const anterior = dadosAnterior.reduce((s, f) => s + valorDevido(f), 0);
    const diferenca = pct(atual, anterior);
    return {
      titulo: 'Faturamento · Mês vs mês anterior',
      metricaDescricao: 'Valor emitido por mês de referência',
      rotuloAtual: mesAtual, rotuloAnterior: mesAnterior,
      valorAtual: atual, valorAnterior: anterior,
      diferencaAbsoluta: atual - anterior, diferencaPercentual: diferenca,
      corSemantica: diferenca === null ? 'neutra' : diferenca >= 0 ? 'positiva' : 'negativa',
    };
  }

  if (tipo === 'inadimplencia') {
    // ESTOQUE de dívida hoje × há 30 dias — NÃO o vencido do mês de referência.
    //
    // A regra anterior comparava o vencido das faturas de SETEMBRO com o de
    // AGOSTO. Mas as faturas do mês corrente mal venceram (26 de 313 em
    // 21/09/2026) enquanto o mês anterior já venceu inteiro: a conta dava uma
    // queda enorme TODO MÊS, por construção. Em 21/09/2026 o card dizia
    // "-78,7%, evolução positiva" enquanto a dívida real subia 40,7%
    // (R$ 652.842 → R$ 918.332). O indicador não estava impreciso — apontava
    // para o lado contrário, e o insight automático reforçava (solicitação 5842).
    //
    // Inadimplência é ESTOQUE, não fluxo: boleto de AGO/2025 não pago continua
    // sendo dívida hoje. Os próprios FILTROS_INICIAIS do painel já partem desse
    // princípio ao usar 'historico' em vez de 'ultimos12'.
    //
    // ⚠️ O valor sozinho exagera: parte da alta é a carteira crescendo (+19,7%
    // de exigível no mesmo período). Por isso a TAXA vai no `extra` — 15,2% →
    // 17,8%, +2,7 p.p., que é a piora real da cobrança, sem o efeito do
    // crescimento.
    const agora = fotoInadimplencia(todosOsDados, hoje);
    const trintaDias = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 30);
    const antes = fotoInadimplencia(todosOsDados, trintaDias);
    const diferenca = pct(agora.vencido, antes.vencido);
    const pp = (agora.taxa - antes.taxa) * 100;
    const sinal = pp > 0 ? '+' : '';

    return {
      titulo: 'Inadimplência · Hoje vs 30 dias atrás',
      metricaDescricao: 'Dívida vencida e não paga, acumulada',
      rotuloAtual: fmtData(hoje), rotuloAnterior: fmtData(trintaDias),
      valorAtual: agora.vencido, valorAnterior: antes.vencido,
      diferencaAbsoluta: agora.vencido - antes.vencido, diferencaPercentual: diferenca,
      // Para inadimplência, subir é PIOR — inverte a semântica de cor do card de faturamento.
      corSemantica: diferenca === null ? 'neutra' : diferenca <= 0 ? 'positiva' : 'negativa',
      extra: `${agora.qtde} boletos vencidos · taxa ${(antes.taxa * 100).toFixed(1)}% → ${(agora.taxa * 100).toFixed(1)}% (${sinal}${pp.toFixed(1)} p.p.)`,
    };
  }

  // boletosEmitidos
  const atual = dadosAtual.length;
  const anterior = dadosAnterior.length;
  const diferenca = pct(atual, anterior);
  const valorAtual = dadosAtual.reduce((s, f) => s + valorDevido(f), 0);
  const ticketMedio = atual > 0 ? valorAtual / atual : 0;
  return {
    titulo: 'Boletos emitidos · Mês vs mês anterior',
    metricaDescricao: 'Quantidade de boletos por mês de referência',
    rotuloAtual: mesAtual, rotuloAnterior: mesAnterior,
    valorAtual: atual, valorAnterior: anterior,
    diferencaAbsoluta: atual - anterior, diferencaPercentual: diferenca,
    corSemantica: diferenca === null ? 'neutra' : diferenca >= 0 ? 'positiva' : 'negativa',
    extra: `Ticket médio do mês: ${fmtBRL(ticketMedio)}`,
  };
}

// Card 4 da seção 8: divide a janela do filtro de período em duas metades
// iguais e nomeia a unidade (semestre p/ janela de 12 meses, trimestre p/ 6).
export function calcularComparativoMetadePeriodo(
  dados: FaturaPainel[],
  janela: { inicio: Date; fim: Date }
): CardComparativo {
  const totalMeses = (janela.fim.getFullYear() - janela.inicio.getFullYear()) * 12 + (janela.fim.getMonth() - janela.inicio.getMonth()) + 1;
  const metade = Math.round(totalMeses / 2);
  const fimMetadeAnterior = new Date(janela.inicio.getFullYear(), janela.inicio.getMonth() + metade - 1, 1);
  const inicioMetadeAtual = new Date(janela.inicio.getFullYear(), janela.inicio.getMonth() + metade, 1);

  const unidade = totalMeses >= 12 ? 'Semestre' : totalMeses >= 6 ? 'Trimestre' : 'Metade do período';

  const noIntervalo = (d: Date, ini: Date, fim: Date) => d.getTime() >= ini.getTime() && d.getTime() <= fim.getTime();
  const vencidosPorMetade = (ini: Date, fim: Date) =>
    dados.filter((f) => {
      if (f.statusDerivado !== 'Vencido') return false;
      const d = mesReferenciaParaData(f.mes_referencia);
      return d ? noIntervalo(d, ini, fim) : false;
    });

  const listaAnterior = vencidosPorMetade(janela.inicio, fimMetadeAnterior);
  const listaAtual = vencidosPorMetade(inicioMetadeAtual, janela.fim);
  const valorAnterior = listaAnterior.reduce((s, f) => s + valorDevido(f), 0);
  const valorAtual = listaAtual.reduce((s, f) => s + valorDevido(f), 0);
  const diferenca = pct(valorAtual, valorAnterior);

  const todosMetade = (ini: Date, fim: Date) =>
    dados.filter((f) => {
      if (!ehCobranca(f)) return false;
      const d = mesReferenciaParaData(f.mes_referencia);
      return d ? noIntervalo(d, ini, fim) : false;
    });
  const emitidoAtual = todosMetade(inicioMetadeAtual, janela.fim).reduce((s, f) => s + valorDevido(f), 0);
  const emitidoAnterior = todosMetade(janela.inicio, fimMetadeAnterior).reduce((s, f) => s + valorDevido(f), 0);
  const taxaAtual = emitidoAtual > 0 ? valorAtual / emitidoAtual : 0;
  const taxaAnterior = emitidoAnterior > 0 ? valorAnterior / emitidoAnterior : 0;

  return {
    titulo: `Inadimplência · ${unidade} vs ${unidade.toLowerCase()} anterior`,
    metricaDescricao: `${fmtMes(inicioMetadeAtual)} a ${fmtMes(janela.fim)} × ${fmtMes(janela.inicio)} a ${fmtMes(fimMetadeAnterior)}`,
    rotuloAtual: `${fmtMes(inicioMetadeAtual)} a ${fmtMes(janela.fim)}`,
    rotuloAnterior: `${fmtMes(janela.inicio)} a ${fmtMes(fimMetadeAnterior)}`,
    valorAtual, valorAnterior,
    diferencaAbsoluta: valorAtual - valorAnterior, diferencaPercentual: diferenca,
    corSemantica: diferenca === null ? 'neutra' : diferenca <= 0 ? 'positiva' : 'negativa',
    extra: `Taxa: ${(taxaAtual * 100).toFixed(1)}% vs ${(taxaAnterior * 100).toFixed(1)}%`,
  };
}
